import { ReactNode, useCallback, useEffect, useRef, useState } from "react"
import { safeParseInt } from "shared/util"
import { Button, FormControlLabel, Stack, Switch, Typography } from "@mui/material"
import { Replay as ReplayIcon } from '@mui/icons-material'
import { NumericInput } from "components/controls/numbericInput"
import { GridColumnDef, GridFilter, GridFilterRange } from "../../types"

export const buildFilterLabel = (
	range: GridFilterRange,
	suffix?: string
) => {
	const labels = []
	if (range.from) {
		labels.push(`${range.from}${suffix ?? ''}${range.fromEq === false ? '超過' : '以上'}`)
	}
	if (range.to) {
		labels.push(`${range.to}${suffix ?? ''}${range.toEq === false ? '未満' : '以下'}`)
	}
	return labels.join('')
}

type RangeFilterListProps<T> = {
	field: string
	fieldDef: GridColumnDef<T>
	filter: GridFilter | undefined
	filters: GridFilter[]
	setFilters: (filters: GridFilter[]) => void
	children?: ReactNode
}
export const RangeFilter = <T,>({
	field,
	fieldDef,
	filter,
	filters,
	setFilters,
	children
}: RangeFilterListProps<T>) => {
	const [from, setFrom] = useState<string>('')
	const [to, setTo] = useState<string>('')

	const inputTimer = useRef<NodeJS.Timeout | null>(null)
	useEffect(
		() => {
			const range = filter?.range
			setFrom(range?.from ? `${range.from}` : '')
			setTo(range?.to ? `${range.to}` : '')
		},
		[filter]
	)

	const setRangeDirect = useCallback(
		(range: GridFilterRange) => {
			if (filter) {
				if (!range.to && !range.from) {
					// remove filter
					setFilters(filters.filter(f => f.field !== filter.field).slice())
				} else {
					// replace filter
					const newFilter: GridFilter = {
						...filter,
						label: buildFilterLabel(range, fieldDef.suffix),
						range
					}
					setFilters(filters.map(f => f.field === filter.field ? newFilter : f).slice())
				}
			} else if (range.from || range.to) {
				// new filter
				setFilters(filters.concat({
					field,
					label: buildFilterLabel(range, fieldDef.suffix),
					range
				}).slice())
			}
		},
		[setFilters, filters, filter, field, fieldDef]
	)
	const setRangeBound = useCallback(
		(range: GridFilterRange) => {
      if (inputTimer.current) clearTimeout(inputTimer.current)
			inputTimer.current = setTimeout(() => {
				setRangeDirect(range)
				inputTimer.current = null
			}, 500)
		},
		[setRangeDirect]
	)
	const setFromText = useCallback(
		(text: string) => {
			let from = undefined
			const trimed = text.trim()
			if (trimed !== '') from = safeParseInt(trimed)
			const range = filter?.range
			setRangeBound({ ...range, from })
			setFrom(text)
		},
		[filter, setRangeBound]
	)
	const setFromEq = useCallback(
		(fromEq: boolean) => {
			const range = filter?.range
			if (!range) return
			setRangeDirect({ ...range, fromEq })
		},
		[filter, setRangeDirect]
	)
	const onResetFrom = useCallback(
		() => {
			const range = filter?.range
			if (!range) return
			setRangeDirect({ ...range, from: undefined })
		},
		[filter, setRangeDirect]
	)

	const setToText = useCallback(
		(text: string) => {
			let to = undefined
			const trimed = text.trim()
			if (trimed !== '') to = safeParseInt(trimed)
			const range = filter?.range
			setRangeBound({ ...range, to })
			setTo(text)
		},
		[filter, setRangeBound]
	)
	const setToEq = useCallback(
		(toEq: boolean) => {
			const range = filter?.range
			if (!range) return
			setRangeDirect({ ...range, toEq })
		},
		[filter, setRangeDirect]
	)
	const onResetTo = useCallback(
		() => {
			const range = filter?.range
			if (!range) return
			setRangeDirect({ ...range, to: undefined })
		},
		[filter, setRangeDirect]
	)
	return (
		<>
			<Stack direction="row" padding={1} spacing={0.5}>
				<Stack direction="column" spacing={1}>
					<Stack direction="row" spacing={0.5} alignItems="center">
						<NumericInput
							state={from}
							setState={setFromText}
							suffix={fieldDef.suffix}
						/>
						<FormControlLabel
							disabled={filter ? false : true}
							control={<Switch checked={filter?.range?.fromEq ?? true} size="small" onChange={(_, checked) => setFromEq(checked)}/>}
							label={<Typography variant="caption" fontSize="small" marginY={-0.5}>{filter?.range?.fromEq !== false ? "以上" : "超過"}</Typography>}
							labelPlacement="bottom"
						/>
					</Stack>

					<Stack direction="row" spacing={1}>
						<Button
							fullWidth
							disabled={filter === undefined || filter.range?.from === undefined}
							variant="outlined"
							size="small"
							startIcon={<ReplayIcon />}
							sx={{ whiteSpace: 'nowrap' }}
							onClick={onResetFrom}
						>
							初期状態に戻す
						</Button>
					</Stack>
				</Stack>
				<Stack direction="column" spacing={1}>
					<Stack direction="row" spacing={0.5} alignItems="center">
						<NumericInput
							state={to}
							setState={setToText}
							suffix={fieldDef.suffix}
						/>
						<FormControlLabel
							disabled={filter ? false : true}
							control={<Switch checked={filter?.range?.toEq ?? true} size="small" onChange={(_, checked) => setToEq(checked)}/>}
							label={<Typography variant="caption" fontSize="small" marginY={-0.5}>{filter?.range?.toEq !== false ? "以下" : "未満"}</Typography>}
							labelPlacement="bottom"
						/>
					</Stack>

					<Stack direction="row" spacing={1}>
						<Button
							fullWidth
							disabled={filter === undefined || filter.range?.to === undefined}
							variant="outlined"
							size="small"
							startIcon={<ReplayIcon />}
							sx={{ whiteSpace: 'nowrap' }}
							onClick={onResetTo}
						>
							初期状態に戻す
						</Button>
					</Stack>
				</Stack>
			</Stack>

			{children}
		</>
	)
}

